/**
 * OAStorefrontApi - Shared helper for Shopify Storefront GraphQL API
 * Used by vue-cart.js and other components for batch product fetching
 */
window.OAStorefrontApi = {
  gid: 'gid://shopify',

  /**
   * Convert resource type and ID to Shopify Global ID format
   * @param {string} resource - Resource type (e.g., 'Product', 'ProductVariant')
   * @param {string|number} id - Numeric ID
   * @returns {string} Global ID (e.g., 'gid://shopify/Product/123')
   */
  toGid: function(resource, id) {
    return this.gid + '/' + resource + '/' + id;
  },

  /**
   * Convert array of IDs to Global IDs
   * @param {string} resource - Resource type
   * @param {Array<string|number>} ids - Array of numeric IDs
   * @returns {Array<string>} Array of Global IDs
   */
  toGids: function(resource, ids) {
    var self = this;
    return ids.map(function(id) { return self.toGid(resource, id); });
  },

  /**
   * Check if value is a Global ID
   * @param {*} value - Value to check
   * @returns {boolean}
   */
  isGid: function(value) {
    return typeof value === 'string' && value.indexOf(this.gid) === 0;
  },

  /**
   * Extract numeric ID from Global ID or return stringified value
   * @param {string|number} value - Global ID or numeric ID
   * @returns {string} Numeric ID as string
   */
  toNumericId: function(value) {
    if (typeof value === 'number') return String(value);
    if (this.isGid(value)) {
      return value.replace(/\D/g, '');
    }
    return String(value);
  },

  /**
   * Shopify Markets country from theme.liquid. Null off sponsorship, where withContext() is a no-op.
   * @returns {string|null} Two-letter country code
   */
  marketCountry: function() {
    var c = window.oaMarketCountry;
    return (typeof c === 'string' && /^[A-Z]{2}$/.test(c)) ? c : null;
  },

  /**
   * Add @inContext(country) so queries resolve in the shopper's market, not the primary one.
   * Inserted before the `{` opening the operation body, scanning past a string-aware variable list;
   * fragments are left alone. Idempotent; unrecognised input is returned unchanged.
   * @param {string} query - GraphQL query string
   * @returns {string} Query with the directive, or the input untouched
   */
  withContext: function(query) {
    var country = this.marketCountry();
    if (!country || typeof query !== 'string') return query;
    if (query.indexOf('@inContext') !== -1) return query;

    var directive = ' @inContext(country: ' + country + ')';
    var m = /(?:^|\})\s*(?:query|mutation)\b/.exec(query);

    // Anonymous `{ ... }` document: promote it to `query @inContext(...) { ... }`.
    if (!m) {
      var brace = query.indexOf('{');
      if (brace === -1) return query;
      return query.slice(0, brace) + 'query' + directive + ' ' + query.slice(brace);
    }

    var i = m.index + m[0].length;
    var depth = 0;
    var inStr = false;
    for (; i < query.length; i++) {
      var ch = query[i];
      if (inStr) {
        if (ch === '\\') i++;
        else if (ch === '"') inStr = false;
        continue;
      }
      if (ch === '"') inStr = true;
      else if (ch === '(') depth++;
      else if (ch === ')') depth--;
      else if (ch === '{' && depth === 0) break;
    }
    if (i >= query.length) return query;
    return query.slice(0, i).replace(/[ \t]+$/, '') + directive + ' ' + query.slice(i);
  },

  /**
   * Execute GraphQL query against Storefront API
   * @param {string} query - GraphQL query string
   * @param {Object} variables - Query variables
   * @returns {Promise<Object|null>} Response data or null on error
   */
  fetch: function(query, variables) {
    return fetch('/api/2025-10/graphql.json', {
      method: 'POST',
      headers: {
        'Accept': 'application/graphql-response+json, application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        // Off `window`, not `this`, so a destructured fetch() can't silently drop the market.
        query: window.OAStorefrontApi.withContext(query),
        variables: variables
      })
    })
    .then(function(r) { return r.json(); })
    .then(function(result) {
      if (result.errors) {
        console.error('Storefront API errors:', result.errors);
        return null;
      }
      return result.data;
    })
    .catch(function(error) {
      console.error('Storefront API fetch error:', error);
      return null;
    });
  }
};
